import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { HookInputProps, outputType } from 'types/hooks';
import { errorMessage } from 'config/constants';
import _ from 'lodash';
import { formatNumWithKMBAlt } from 'utils/helperMethods';
interface FetchLenseConfigProp extends HookInputProps {}
function useFetchOfferData({
    url,
    method,
    onComplete,
    onError,
    queryKey,
    variables = {},
    queryConfig = {},
}: FetchLenseConfigProp) {
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<null | string>(null);
    const [data, setData] = useState<any | null>(null);
    // Queries
    const {
        data: apiData,
        isFetching,
        isSuccess,
        fetchStatus,
        error: apiError,
    } = useQuery({
        queryKey,
        queryFn: async () => {
            //only set data to null if function is being called outside/without infinite scroll
            //if offset is equal to default offset, it means request is coming without infinite scroll
            if (variables?.offset === 0) {
                setData(null);
                setError(null);
            }
            setLoading(true);
            try {
                const data = await axios({
                    method,
                    url,
                    data: { ...variables },
                });
                return data;
            } catch (error) {
                onFetchError(error);
            }
        },
        ...queryConfig,
    });
    const apiLoading = isFetching || fetchStatus === 'paused';
    const apiSuccess = !apiLoading && !apiError && isSuccess;
    //error handling
    if (!apiLoading && apiError && !error) {
        if (onError) return onError(apiError);
        onFetchError(apiError);
    }
    useEffect(() => {
        //success handling
        if (apiSuccess && apiData?.data) {
            if (error) setError(null);
            if (onComplete) return onComplete(apiData?.data);
            onFetchComplete(apiData?.data);
        } else if (apiSuccess && !apiData?.data) {
            setLoading(false);
            setData(null);
        }
    }, [apiData?.data, apiSuccess]);
    //called when fetching is complete
    function onFetchComplete(payload: any) {
        try {
            if (payload && payload.length) {
                const parsedData = payload?.map((item: any) => {
                    return {
                        offer: item?.offer,
                        uplift: {
                            value: formatNumWithKMBAlt(item?.tpo_uplift_percent ?? 0, 2, 2),
                            isPositive: item?.tpo_uplift_percent > 0,
                        },
                        mpic: { value: formatNumWithKMBAlt(item?.mpic ?? 0, 2, 2), isPositive: item?.mpic > 0 },
                        incrementalSales: {
                            value: formatNumWithKMBAlt(item?.incremental_sales ?? 0, 2, 2),
                            isPositive: item?.incremental_sales > 0,
                        },
                    };
                });
                //setting all required states
                setData(parsedData);
            } else {
                setData([]);
            }
        } catch (error: any) {
            console.log(error, error.message);
            console.error(errorMessage, error);
            onFetchError(error);
        } finally {
            setLoading(false);
        }
    }
    //called when API throws an error
    function onFetchError(error: any = errorMessage) {
        setData(null);
        setLoading(false);
        setError(error);
    }
    const outputData: outputType = {
        loading,
        error,
        data,
    };
    return outputData;
}
export { useFetchOfferData };
